import React, { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'APP/Hooks/redux'
import { SectionList, View } from 'react-native'
import styled from 'styled-components/native'

import { ResponsiveScrollView } from 'APP/Converse/Layout'
import Typography from 'APP/Converse/Typography'
import I18n from 'APP/I18n'
import ServiceGroupTile from 'APP/Components/ServiceGroupTile'
import PromotedServiceGroupTile from 'APP/Components/PromotedServiceGroupTile'
import { contentActions } from 'APP/Store/Content'
import { selectExternalServices } from 'APP/Redux/PatientRedux'
import { filterServiceGroupsAndServicesForEligibility } from 'APP/Lib/ContentHelpers'
import type { ServiceGroupSection, ServiceGroups } from 'APP/Store/Content/types'

const CtaContainer = styled(View)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  flex-direction: column;
`

const CtaPromotedContainer = styled(View)`
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

const StyledSectionList = styled.SectionList`
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin * 1.25}px;
` as unknown as typeof SectionList

const SectionHeader = styled(View)`
  align-self: flex-start;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
`

const PromotedSectionList = styled(View)`
  flex-direction: row;
  align-items: stretch;
  margin-horizontal: ${({ theme }) => -theme.metrics.baseMargin / 2}px;
`

const MainHeader = styled(Typography)`
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin * 1.25}px;
  text-align: center;
  align-self: center;
`

interface Section {
  key: string
  title: ServiceGroupSection['title']
  data: ServiceGroups
}

const GetCareScreen: React.FC = () => {
  const dispatch = useDispatch()
  const serviceGroups = useSelector((state) => state.content.serviceGroups)
  const profile = useSelector((state) => state.patient.profile)
  const externalServices = useSelector(selectExternalServices)
  const eligibleServices = profile?.eligibleServices

  useEffect(() => {
    dispatch(contentActions.getServiceGroups())
  }, [dispatch])

  const serviceGroupSections = useMemo<Section[]>(() => {
    const data = (serviceGroups?.data ?? {}) as unknown as Record<string, ServiceGroupSection>
    return Object.entries(data).map(([key, value]) => ({
      key,
      title: value?.title,
      data: Array.isArray(value.serviceGroups) ? value.serviceGroups : [],
    }))
  }, [serviceGroups])

  const filteredServiceGroupSections = useMemo(() => {
    return serviceGroupSections
      .map((section) => {
        const filteredData = filterServiceGroupsAndServicesForEligibility(
          section.data,
          eligibleServices,
          externalServices
        )

        return {
          ...section,
          data: filteredData,
          eligibleServices,
          externalServices,
        }
      })
      .filter((section) => section.data.length > 0)
  }, [serviceGroupSections, externalServices, eligibleServices])

  const isPromotedSection = (section: (typeof filteredServiceGroupSections)[number]) => {
    return (
      filteredServiceGroupSections.indexOf(section) === 0 &&
      filteredServiceGroupSections[0].key === serviceGroupSections[0].key
    )
  }

  const ServiceGroupHeader = ({ title }: { title: Section['title'] }) =>
    title && (
      <SectionHeader>
        <Typography variant="h4"> {title} </Typography>
      </SectionHeader>
    )

  return (
    <ResponsiveScrollView testID="GET_CARE_VIEW">
      {I18n.t('GetCareScreen.header') && (
        <MainHeader variant="h3">{I18n.t('GetCareScreen.header')}</MainHeader>
      )}
      {Boolean(filteredServiceGroupSections.length) && (
        <StyledSectionList
          sections={filteredServiceGroupSections}
          keyExtractor={(item, index) => item.id + index}
          renderItem={({ item, section }) => {
            if (isPromotedSection(section)) {
              return null
            } else {
              return (
                <CtaContainer>
                  <ServiceGroupTile key={item.id} item={item} analyticsScreenName={'chatScreen'} />
                </CtaContainer>
              )
            }
          }}
          renderSectionHeader={({ section }) => {
            if (isPromotedSection(section)) {
              return (
                <>
                  <ServiceGroupHeader title={section.title} />
                  <PromotedSectionList style={{ flex: 1 }}>
                    {section.data.map((item, index) => (
                      <CtaPromotedContainer style={{ flex: 1 }} key={index}>
                        <PromotedServiceGroupTile item={item} analyticsScreenName={'chatScreen'} />
                      </CtaPromotedContainer>
                    ))}
                  </PromotedSectionList>
                </>
              )
            } else {
              return <ServiceGroupHeader title={section.title} />
            }
          }}
        />
      )}
    </ResponsiveScrollView>
  )
}

export default GetCareScreen
